import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format, parse } from 'date-fns';
import { RootState, AppDispatch } from 'src/slices/store';
import {
  closePanel,
  setDateRange1,
  setDateRange2,
  setSelectedMetrics,
  setDataBy,
} from 'src/slices/Compare/reducer';
import { fetchCompareData } from 'src/slices/Compare/thunk';
import { getPermittedCompareMetrics, getAvailableDataBy, computeDateBounds, parseLocalDateStr } from './compareUtils';
import { selectExchangeByName } from 'src/slices/Report/reducer';
import CustomDatePicker from 'src/common/CustomDatePicker';
import TableCustomLoader from 'src/components/Common/TableCustomLoader';

// Set to a number to enable minimum-day validation, or false to disable.
const MIN_RANGE_DAYS: number | false = false;

function toDate(dateStr: string): Date {
  if (!dateStr) return new Date();
  return parse(dateStr, 'yyyy-MM-dd', new Date());
}

function toDateString(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

const CompareSelectionScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const openExchange = useSelector((state: RootState) => state.compare.openExchange);
  const exState = useSelector((state: RootState) =>
    openExchange ? state.compare.exchanges[openExchange] : null
  );

  const reportExchange = useSelector((state: RootState) =>
    openExchange ? selectExchangeByName(state.reports, openExchange) : undefined
  );

  const exchangeName     = openExchange ?? '';
  const dateRange1       = exState?.dateRange1       ?? { start: '', end: '' };
  const dateRange2       = exState?.dateRange2       ?? { start: '', end: '' };
  const selectedMetrics  = exState?.selectedMetrics  ?? [];
  const lineItemIds      = exState?.lineItemIds      ?? [];
  const dataBy           = exState?.dataBy           ?? 'week';
  const loading          = exState?.loading          ?? false;

  const permittedMetrics = useMemo(
    () => getPermittedCompareMetrics(reportExchange?.display_metrics ?? []),
    [reportExchange?.display_metrics]
  );

  // Order bounds — read directly from stored Redux state (set when the panel was opened)
  const orderMinDate = useMemo(
    () => parseLocalDateStr(exState?.orderStart ?? ''),
    [exState?.orderStart]
  );
  const orderMaxDate = useMemo(
    () => parseLocalDateStr(exState?.orderEnd ?? ''),
    [exState?.orderEnd]
  );

  // Overlap-preventing bounds
  const { rangesConflict } = useMemo(
    () => computeDateBounds(dateRange1, dateRange2, orderMinDate, orderMaxDate),
    [dateRange1, dateRange2, orderMinDate, orderMaxDate]
  );

  const allDatesSet =
    !!dateRange1.start && !!dateRange1.end &&
    !!dateRange2.start && !!dateRange2.end;

  const canCompare = allDatesSet && selectedMetrics.length > 0 && !loading && !rangesConflict;

  const date1 = useMemo(() => ({
    startDate: toDate(dateRange1.start),
    endDate:   toDate(dateRange1.end),
    key: 'selection',
  }), [dateRange1.start, dateRange1.end]);

  const date2 = useMemo(() => ({
    startDate: toDate(dateRange2.start),
    endDate:   toDate(dateRange2.end),
    key: 'selection',
  }), [dateRange2.start, dateRange2.end]);

  const handleDate1Change = (ranges: any) => {
    const { startDate, endDate } = ranges.selection;
    dispatch(setDateRange1({ exchangeName, range: { start: toDateString(startDate), end: toDateString(endDate) } }));
  };

  const handleDate2Change = (ranges: any) => {
    const { startDate, endDate } = ranges.selection;
    dispatch(setDateRange2({ exchangeName, range: { start: toDateString(startDate), end: toDateString(endDate) } }));
  };

  const allSelected = permittedMetrics.length > 0 && permittedMetrics.every(m => selectedMetrics.includes(m.key));

  const handleSelectAll = () => {
    const updated = allSelected ? [] : permittedMetrics.map(m => m.key);
    dispatch(setSelectedMetrics({ exchangeName, metrics: updated }));
  };

  const handleMetricToggle = (key: string) => {
    const updated = selectedMetrics.includes(key)
      ? selectedMetrics.filter(k => k !== key)
      : [...selectedMetrics, key];
    dispatch(setSelectedMetrics({ exchangeName, metrics: updated }));
  };

  const handleCompare = () => {
    if (!canCompare) return;

    // Snap dataBy to a valid option for these ranges before fetching.
    const available = getAvailableDataBy(dateRange1, dateRange2);
    let effectiveDataBy = dataBy;
    if (!available.includes(dataBy)) {
      effectiveDataBy = available.includes('week') ? 'week' : available[0];
      dispatch(setDataBy({ exchangeName, dataBy: effectiveDataBy }));
    }

    dispatch(fetchCompareData({
      exchangeName,
      lineItemIds,
      range1Start: dateRange1.start,
      range1End:   dateRange1.end,
      range2Start: dateRange2.start,
      range2End:   dateRange2.end,
      selectedMetrics,
      dataBy: effectiveDataBy,
    }));
  };

  let rangeWarning: string | null = null;
  if (MIN_RANGE_DAYS !== false && allDatesSet) {
    const days1 = (new Date(dateRange1.end).getTime() - new Date(dateRange1.start).getTime()) / 86_400_000;
    const days2 = (new Date(dateRange2.end).getTime() - new Date(dateRange2.start).getTime()) / 86_400_000;
    if (days1 < MIN_RANGE_DAYS || days2 < MIN_RANGE_DAYS) {
      rangeWarning = `Ranges shorter than ${MIN_RANGE_DAYS} days may produce limited chart data.`;
    }
  }

  if (loading) {
    return <TableCustomLoader length={3} message="Fetching compare data" />;
  }

  return (
    <div>
      {/* Date Range (Previous) */}
      <div className="compare-date-section">
        <div className="compare-date-label fw-medium">Date Range (Previous)</div>
        <CustomDatePicker
          date={date2}
          handleDateChange={handleDate2Change}
          orderMinDate={orderMinDate}
          orderMaxDate={orderMaxDate}
        />
      </div>

      {/* Date Range (Current) */}
      <div className="compare-date-section">
        <div className="compare-date-label fw-medium">Date Range (Current)</div>
        <CustomDatePicker
          date={date1}
          handleDateChange={handleDate1Change}
          orderMinDate={orderMinDate}
          orderMaxDate={orderMaxDate}
        />
      </div>

      {rangesConflict && (
        <div className="compare-conflict-error">
          <i className="uil uil-exclamation-triangle" />
          Previous Date Range conflicts with Current Date Range. Please re-select.
        </div>
      )}

      {rangeWarning && !rangesConflict && (
        <div className="alert alert-warning py-2 px-3 mb-3 small" role="alert">
          {rangeWarning}
        </div>
      )}

      {/* Metric selection */}
      <div className="mb-3">
        <h6 className="compare-metrics-label fw-medium mb-1">Select Metrics</h6>
        <p className="text-muted small mb-2">Select at least one metric to compare</p>
        <div className="compare-select-all-row mb-2">
          <div className="metric-control-wrapper">
            <input
              id="compare-select-all"
              className="form-check-input checkbox-input"
              type="checkbox"
              checked={allSelected}
              onChange={handleSelectAll}
              onClick={(e) => e.stopPropagation()}
            />
            <label
              htmlFor="compare-select-all"
              className="checkbox-label custom-label"
              onClick={(e) => e.preventDefault()}
            >
              <span className="metric-name">{allSelected ? 'Deselect All' : 'Select All'}</span>
            </label>
          </div>
        </div>
        <div className="compare-metrics-grid">
          {permittedMetrics.map((metric) => {
            const isSelected = selectedMetrics.includes(metric.key);
            return (
              <div
                key={metric.key}
                className="metric-tile custom-tile compare-metric-tile"
                onClick={() => handleMetricToggle(metric.key)}
              >
                <div className="metric-control-wrapper">
                  <input
                    id={`compare-metric-${metric.key}`}
                    className="form-check-input checkbox-input"
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleMetricToggle(metric.key)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label
                    htmlFor={`compare-metric-${metric.key}`}
                    className="checkbox-label custom-label"
                    onClick={(e) => e.preventDefault()}
                  >
                    <span className="metric-name">{metric.label}</span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="compare-selection-footer">
        <button
          type="button"
          className="btn btn-outline-secondary button-secondary"
          onClick={() => dispatch(closePanel())}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary button-primary"
          disabled={!canCompare}
          onClick={handleCompare}
        >
          Compare
        </button>
      </div>
    </div>
  );
};

export default CompareSelectionScreen;
